import React, { useMemo, useState } from 'react';

import {
	AsaphDialog,
	AsaphDialogHeader,
	AsaphDialogHeaderButton,
} from '@/components/shared/Dialog';
import { SmallTeamSongCard } from '@/components/SmallTeamSongCard';
import { SetlistSummaryCalendar } from '@/components/SetlistSummaryCard';
import { IndexIndicator } from '@/components/shared/IndexIndicator';
import { Loader } from '@/components/shared/Loader';
import { PANEL_TRANSITION_TIMEOUT } from '@/config/constants';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { useAuth } from '@/providers/AuthProvider';
import { usePermissions } from '@/providers/PermissionsProvider';
import { useSetlists } from '@/providers/SetlistsProvider';
import { useServices } from '@/providers/ServicesProvider';
import { useTeamSongs } from '@/providers/TeamSongsProvider';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';

import styles from './FeedbackPanel.module.scss';
import { TeamSongScorePage } from './TeamSongScorePage';
import { TeamSongSubmissionPage } from './TeamSongSubmissionPage';

export type TeamSongFeedbackSubmission = {
	teamSongId: string;
	personalScore: number;
	teamScore: number;
	congregationalScore: number;
};

export type TeamSongFeedbackScoresMap = Record<
	string,
	TeamSongFeedbackSubmission
>;

export type FeedbackPanelCloseParams = {
	submittedFeedback?: boolean;
};

export interface FeedbackPanelProps {
	onClose: (params: FeedbackPanelCloseParams) => void;
	config: {
		setlistId: string;
		noToast?: boolean;
	};
}

export const FeedbackPanel: React.FC<FeedbackPanelProps> = ({
	onClose,
	config,
}) => {
	const { user } = useAuth();
	const { hasPermission } = usePermissions();
	const { setlists } = useSetlists();
	const { services } = useServices();
	const { findTeamSongsByIdReduced, isLoading } = useTeamSongs();
	const { enqueueSnackbar } = useSnackbar();

	const fireErrorEvent = useAnalyticsEvent('Error');
	const fireNavigateSong = useAnalyticsEvent('Feedback_Panel_Navigate_Song');
	const fireAbandoned = useAnalyticsEvent('Feedback_Panel_Abandoned');

	const [teamSongFeedbackScoresMap, setTeamSongFeedbackScoresMap] =
		useState<TeamSongFeedbackScoresMap>({});
	const [currentTeamSongFeedbackPageIdx, setCurrentTeamSongFeedbackPageIdx] =
		useState<number>(0);

	const setlist = useMemo(
		() => setlists.find((s) => s.id === config.setlistId),
		[setlists, config.setlistId],
	);

	const setlistTeamSongs = useMemo(() => {
		if (!setlist) {
			return [];
		}

		const populatedTeamSongs = findTeamSongsByIdReduced(
			setlist.teamSongIds,
		);

		setTeamSongFeedbackScoresMap(
			populatedTeamSongs.reduce<TeamSongFeedbackScoresMap>(
				(acc, teamSong) => {
					return {
						...acc,
						[teamSong.id]: {
							...emptyTeamSongFeedback,
							teamSongId: teamSong.id,
						},
					};
				},
				{},
			),
		);

		return populatedTeamSongs;
		// We need to trigger a recalculation when the data changes
		// eslint-disable-next-line react-hooks/exhaustive-deps
		// If we want to make the feedback "live" and react to updates
		// in the team songs list, add `dataUpdatedAt` to the deps array
	}, [setlist, findTeamSongsByIdReduced]);

	const currentTeamSong: (typeof setlistTeamSongs)[0] | undefined =
		setlistTeamSongs[currentTeamSongFeedbackPageIdx];
	const lastPage =
		currentTeamSongFeedbackPageIdx > setlistTeamSongs.length - 1;

	const handleNavigate = React.useCallback((i: number) => {
		setCurrentTeamSongFeedbackPageIdx(i);
		fireNavigateSong();
	}, [fireNavigateSong]);

	const handleNavigateDispatch: React.Dispatch<React.SetStateAction<number>> = React.useCallback(
		(action) => {
			const nextIdx = typeof action === 'function'
				? action(currentTeamSongFeedbackPageIdx)
				: action;
			handleNavigate(nextIdx);
		},
		[handleNavigate, currentTeamSongFeedbackPageIdx],
	);

	const handleClose = React.useCallback((params: FeedbackPanelCloseParams) => {
		if (!lastPage) {
			fireAbandoned();
		}
		onClose(params);
	}, [lastPage, fireAbandoned, onClose]);

	if (!setlist) {
		onClose({});
		enqueueSnackbar('Something went wrong with starting this feedback.');
		fireErrorEvent({
			name: 'OpenFeedbackError',
			details: `Setlist not found using id ${config.setlistId}`,
		});
		return null;
	}

	return (
		<AsaphDialog
			open={true}
			onClose={() => handleClose({})}
			paperClassName={styles['panel']}
		>
			<div className={styles['toolbar']}>
				<AsaphDialogHeader
					leadingButton={{
						needsFadeIn: true,
						fadeTimeout: PANEL_TRANSITION_TIMEOUT,
						component: (
							<AsaphDialogHeaderButton
								type="text"
								label="Cancel"
								handlePress={() => handleClose({})}
							/>
						),
					}}
					title={{
						needsFadeIn: true,
						fadeTimeout: PANEL_TRANSITION_TIMEOUT,
						component: (
							<div className={styles['dialog-title']}>
								<SetlistSummaryCalendar
									setlist={setlist}
									size="sm"
								/>
								<span>Give Feedback</span>
							</div>
						),
					}}
					trailingButton={{
						needsFadeIn: true,
						fadeTimeout: PANEL_TRANSITION_TIMEOUT,
						component: '',
					}}
				/>
			</div>
			<section className={styles['content']}>
				<div className={styles['top']}>
					<IndexIndicator
						className={styles['indicator']}
						dotClassName={styles['indicator-dot']}
						total={setlistTeamSongs.length + 1}
						current={currentTeamSongFeedbackPageIdx}
						variant="stretched"
						onIndicatorClick={(i) => handleNavigate(i)}
						hasHover
					/>

					<div className={styles['subtitle']}>
						{services.find((s) => s.id === setlist.serviceId)?.name ?? 'Service'}
						{' at '}
						{format(new Date(setlist.startAt), 'h:mmaaa')}
						{', '}
						{format(new Date(setlist.startAt), 'MMMM d yyyy')}
					</div>

					{!lastPage && currentTeamSong && (
						<SmallTeamSongCard
							teamSongId={currentTeamSong.id}
							className={styles['song-card']}
							noClick
							displayKey
						/>
					)}
					{lastPage && (
						<div className={styles['submission-title']}>
							Done! Happy with your feedback?
						</div>
					)}
				</div>

				<div className={styles['bottom']}>
					<div className={styles['bottom-wrapper']}>
						{isLoading ? (
							<Loader />
						) : lastPage ? (
							<TeamSongSubmissionPage
								onClose={onClose}
								setlistId={setlist.id}
								setlistTeamSongs={setlistTeamSongs}
								teamSongFeedbackScoresMap={
									teamSongFeedbackScoresMap
								}
								setCurrentTeamSongFeedbackPageIdx={
									handleNavigateDispatch
								}
								noToast={config.noToast}
							/>
						) : (
							<TeamSongScorePage
								key={`team-score-page=${currentTeamSongFeedbackPageIdx}`}
								teamSong={currentTeamSong}
								teamSongSubmission={
									teamSongFeedbackScoresMap[
										currentTeamSong?.id
									]
								}
								currentTeamSongFeedbackPageIdx={
									currentTeamSongFeedbackPageIdx
								}
								setTeamSongFeedbackScoresMap={
									setTeamSongFeedbackScoresMap
								}
								setCurrentTeamSongFeedbackPageIdx={
									handleNavigateDispatch
								}
							/>
						)}
					</div>
				</div>
			</section>
		</AsaphDialog>
	);
};

export const emptyTeamSongFeedback: TeamSongFeedbackSubmission = {
	congregationalScore: 3,
	personalScore: 3,
	teamScore: 3,
	teamSongId: '',
};
